import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  BookOpen, Calendar, Users, Clock, Award,
  Download, Plus, Filter, Play, CheckCircle2,
  FileText, Star, BarChart2, TrendingUp, Target,
  GraduationCap, ChevronRight, ChevronDown, X
} from 'lucide-react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import { candidatesData } from '../data/mockData';

const CourseCard = ({ course, onEnroll, onViewDetails }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const getStatusColor = (status) => {
    switch (status) {
      case 'In Progress': return 'bg-blue-100 text-blue-700';
      case 'Completed': return 'bg-green-100 text-green-700';
      case 'Upcoming': return 'bg-amber-100 text-amber-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <motion.div
      whileHover={{ scale: 1.01 }}
      className="bg-white rounded-xl shadow-sm overflow-hidden"
    >
      <div className="p-6">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h3 className="font-semibold text-gray-900">{course.title}</h3>
            <p className="text-sm text-gray-500">{course.description}</p>
          </div>
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(course.status)}`}>
            {course.status}
          </span>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-4">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Users className="w-4 h-4" />
            {course.enrolled}/{course.capacity} enrolled
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Clock className="w-4 h-4" />
            {course.duration}
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Calendar className="w-4 h-4" />
            Starts {new Date(course.startDate).toLocaleDateString()}
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <GraduationCap className="w-4 h-4" />
            {course.level}
          </div>
        </div>

        <div className="flex gap-2">
          <button
            onClick={() => onEnroll(course)}
            className="flex-1 py-2 text-indigo-600 bg-indigo-50 rounded-lg hover:bg-indigo-100 
              transition-colors text-sm font-medium"
          >
            Enroll Now
          </button>
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-50"
          >
            {isExpanded ? (
              <ChevronDown className="w-5 h-5" />
            ) : (
              <ChevronRight className="w-5 h-5" />
            )}
          </button>
        </div>
      </div>

      {isExpanded && (
        <div className="px-6 pb-6 space-y-4 border-t mt-4 pt-4">
          {/* Prerequisites */}
          <div>
            <h4 className="text-sm font-medium text-gray-900 mb-2">Prerequisites</h4>
            <ul className="space-y-1">
              {course.prerequisites.map((prereq, index) => (
                <li key={index} className="text-sm text-gray-600 flex items-center gap-2">
                  <div className="w-1 h-1 bg-gray-400 rounded-full" />
                  {prereq}
                </li>
              ))}
            </ul>
          </div>

          {/* Modules */}
          <div>
            <h4 className="text-sm font-medium text-gray-900 mb-2">Course Modules</h4>
            <div className="space-y-2">
              {course.modules.map((module, index) => (
                <div key={index} className="bg-gray-50 p-3 rounded-lg">
                  <div className="font-medium text-sm text-gray-900">{module.title}</div>
                  <div className="text-sm text-gray-500">Duration: {module.duration}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Assessments */}
          <div>
            <h4 className="text-sm font-medium text-gray-900 mb-2">Assessments</h4>
            <div className="space-y-2">
              {course.assessments.map((assessment, index) => (
                <div key={index} className="flex justify-between items-center text-sm">
                  <span className="text-gray-600">{assessment.type}</span>
                  <span className="text-gray-900 font-medium">{assessment.weight}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
};

const ProgressCard = ({ progress }) => {
  const percentage = (progress.completed / progress.total) * 100;

  return (
    <div className="bg-white rounded-xl p-6 shadow-sm">
      <div className="flex justify-between items-start mb-4">
        <div>
          <h3 className="font-semibold text-gray-900">{progress.title}</h3>
          <p className="text-sm text-gray-500">{progress.description}</p>
        </div>
        <span className="text-2xl font-bold text-indigo-600">{percentage.toFixed(0)}%</span>
      </div>

      <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden mb-4">
        <div
          className="h-full bg-indigo-600"
          style={{ width: `${percentage}%` }}
        />
      </div>

      <div className="flex justify-between text-sm text-gray-500">
        <span>{progress.completed} completed</span>
        <span>{progress.total} total</span>
      </div>
    </div>
  );
};

const TrainingModule = ({ isMobile }) => {
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const handleEnroll = (course) => {
    console.log('Enrolling in course:', course);
    // Add enrollment logic here
  };

  const handleViewDetails = (course) => {
    console.log('Viewing course details:', course);
    // Add view details logic here
  };

  const progressStats = [
    {
      title: 'Courses Completed',
      description: 'Total courses finished',
      completed: 12,
      total: 15
    },
    {
      title: 'Current Progress',
      description: 'Active courses',
      completed: 3,
      total: 5
    },
    {
      title: 'Certifications',
      description: 'Professional certificates',
      completed: 4,
      total: 5
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header Actions */}
      <div className="flex flex-wrap gap-4 items-center justify-between">
        <div className="flex items-center gap-4">
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500"
          >
            <option value="all">All Categories</option>
            <option value="technical">Technical</option>
            <option value="soft-skills">Soft Skills</option>
            <option value="leadership">Leadership</option>
            <option value="compliance">Compliance</option>
          </select>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500"
          >
            <option value="all">All Status</option>
            <option value="upcoming">Upcoming</option>
            <option value="in-progress">In Progress</option>
            <option value="completed">Completed</option>
          </select>
        </div>
        <div className="flex gap-2">
          <button className="px-4 py-2 border border-gray-200 rounded-xl hover:bg-gray-50 
            flex items-center gap-2">
            <Download className="w-4 h-4" />
            Export Report
          </button>
          <button className="px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 
            flex items-center gap-2">
            <Plus className="w-4 h-4" />
            Add Course
          </button>
        </div>
      </div>

      {/* Progress Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {progressStats.map((stat, index) => (
          <ProgressCard key={index} progress={stat} />
        ))}
      </div>

      {/* Available Courses */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900">Available Courses</h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {candidatesData.trainingCourses.map((course, index) => (
            <CourseCard
              key={index}
              course={course}
              onEnroll={handleEnroll}
              onViewDetails={handleViewDetails}
            />
          ))}
        </div>
      </div>

      {/* Training Analytics */}
      <div className="bg-white rounded-xl p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">Training Progress Analytics</h3>
        <div className="h-[300px]">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={[
              { month: 'Jan', completion: 85 },
              { month: 'Feb', completion: 88 },
              { month: 'Mar', completion: 92 },
              { month: 'Apr', completion: 90 },
              { month: 'May', completion: 95 }
            ]}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip />
              <Line
                type="monotone"
                dataKey="completion"
                stroke="#4F46E5"
                strokeWidth={2}
                dot={{ r: 4 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default TrainingModule; 